import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, Mail } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import Button from '@components/common/Button';
import { useCreateVendor, useUpdateVendor, useVendorById } from '@api/hooks/useVendors';
import { VendorForm } from '@components/Vendors';
import { useInviteVendor } from '@api/hooks/useVendorPortal';
import type { CreateVendorRequest, CreateVendorResponse } from '@schemas/api';

export const VendorDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { vendorId } = useParams<{ vendorId: string }>();

  const isEditMode = Boolean(vendorId);
  const { data: vendor, isLoading: isLoadingVendor } = useVendorById(vendorId || '');
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor(vendorId || '');
  const inviteVendor = useInviteVendor();

  const [createdVendor, setCreatedVendor] = useState<CreateVendorResponse | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');

  const handleSubmit = async (data: CreateVendorRequest) => {
    if (isEditMode && vendorId) {
      await updateVendor.mutateAsync(data);
      navigate('/records?type=vendor');
    } else {
      const result = await createVendor.mutateAsync(data);
      setCreatedVendor(result);
      setInviteEmail(result.email ?? '');
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createdVendor?.id || !inviteEmail) return;
    await inviteVendor.mutateAsync({ vendor_record_id: createdVendor.id, email: inviteEmail });
    navigate('/records?type=vendor');
  };

  const isSaving = createVendor.isPending || updateVendor.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/records?type=vendor')}
            className="mb-4 inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Vendors
          </Button>
          <h1 className="text-3xl font-bold text-content-primary dark:text-content-primary-dark">
            {isEditMode ? 'Edit Vendor' : 'Create Vendor'}
          </h1>
          <p className="mt-1 text-content-secondary dark:text-content-secondary-dark">
            Manage the vendor profile, including billing, tax, and contact details.
          </p>
        </div>
      </div>

      {createdVendor ? (
        <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-6 space-y-5">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-900 dark:text-green-100">
                {createdVendor.legal_name} has been created.
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Would you like to invite this vendor to access EZ Flow as a portal user?
              </p>
            </div>
          </div>

          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Vendor email
              </label>
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="vendor@company.com"
                className="w-full max-w-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/records?type=vendor')}
              >
                Skip for now
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={inviteVendor.isPending || !inviteEmail}
              >
                <span className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {inviteVendor.isPending ? 'Sending…' : 'Send Invitation'}
                </span>
              </Button>
            </div>
          </form>
        </div>
      ) : isEditMode && isLoadingVendor ? (
        <div className="rounded-xl border border-default dark:border-default-dark bg-surface dark:bg-surface-dark p-6 text-sm text-content-secondary dark:text-content-secondary-dark">
          Loading vendor details...
        </div>
      ) : (
        <VendorForm
          initialData={vendor}
          onSubmit={handleSubmit}
          isLoading={isSaving}
          onCancel={() => navigate('/records?type=vendor')}
        />
      )}
    </div>
  );
};
